// WHAT AN EXCHANGE PRICE IS ACTUALLY WORTH, in one place.
//
// A back price of 2.00 on Betfair does not pay 2.00: commission comes off the
// win, so the punter's real return is 1 + 1.00 x (1 - 0.05) = 1.95. Believing
// the quote hands every figure built on it 5% of a profit that never existed.
//
// THREE MODULES USED TO MAKE THAT CORRECTION AND ONE DID NOT. `sharpAnchor`
// deflated before it devigged, `outliers` deflated the whole board before it
// looked at it, and `universe.consensus` — the function both of them call, and
// the one every strategy and every board cell reads — did not deflate at all.
// So the exchange voted gross of commission in the market's own fair price and
// net of it everywhere else, and the same match had two fair prices depending
// on which door you came in by (engine review #14).
//
// It is fixed by moving the correction DOWN, into `consensus` and `bookFair`
// themselves, which means the list of exchanges and the commission figure have
// to live below them. This module is that floor: a LEAF with no imports at all,
// so `universe.js` can read it without a cycle and `sharpAnchor.js` and
// `outliers.js` can go on re-exporting the same two constants they always did.

/** Books that charge commission on winnings, and so need deflating first. */
export const EXCHANGES = ['betfair_ex_au'];

/** What an exchange takes out of a winning bet. */
export const COMMISSION = 0.05;

/** Is this book an exchange, and so quoting a price it does not fully pay? */
export const isExchange = (bookmaker) => EXCHANGES.includes(bookmaker);

/** A price net of commission where the book charges one. */
export const netPrice = (price, bookmaker, commission = COMMISSION) =>
  (isExchange(bookmaker) && Number.isFinite(price)
    ? 1 + (price - 1) * (1 - commission)
    : price);

/**
 * The match with every exchange price deflated to what it actually returns.
 *
 * Still here, and still used, for the one thing that reads PRICES rather than
 * probabilities: `arbsFor` stakes a dutch across the board and the dollars it
 * splits have to be the dollars that come back. Everything that goes through
 * `consensus` or `bookFair` gets the same correction applied inside them now,
 * and must NOT be handed a board that has already had it — that would take the
 * commission out twice.
 */
export function deflated(match, commission = COMMISSION) {
  const prices = match?.prices;
  if (!prices) return match;
  let touched = false;
  const out = {};
  for (const [selection, rows] of Object.entries(prices)) {
    out[selection] = rows.map((row) => {
      if (!isExchange(row.bookmaker)) return row;
      touched = true;
      return { ...row, price: netPrice(row.price, row.bookmaker, commission) };
    });
  }
  return touched ? { ...match, prices: out } : match;
}
